export type DelayStatus = 'on-time' | 'delayed' | 'significant-delay'
export type CommunicationStatus = 'active' | 'inactive'

export type VendorCategory =
  | 'merch'
  | 'bookbox'
  | 'author-direct'
  | 'service'
  | 'creative'

export interface FulfillmentTransparency {
  vendorCategory?: VendorCategory
  averageProductionTime: string
  currentCustomerExpectation: string
  shippingOrigin: string
  subscriptionTiming?: string
  queueStatus?: 'open' | 'limited' | 'closed'
  queueCapacity?: string
  bookingWindow?: string
  lastUpdated: string
  delayStatus: DelayStatus
  communicationStatus: CommunicationStatus
  daysSinceLastUpdate: number
}

export interface VendorUpdate {
  id: string
  type: 'delay' | 'production-update' | 'shipping-update' | 'issue' | 'resolved'
  message: string
  estimatedNewTimeline?: string
  datePosted: string
  affectedScope: string
  affectedProductIds?: string[]
  affectedCollectionIds?: string[]
  batchIdentifier?: string
  isGlobal?: boolean
  is_pinned?: boolean
  is_edited?: boolean
  edited_at?: string
  is_deleted_soft?: boolean
  resolved_from_update_id?: string
}

export interface VendorFulfillmentSettings {
  averageProductionTime: string
  currentCustomerExpectation: string
  shippingOrigin: string
  subscriptionTiming?: string
  queueStatus?: 'open' | 'limited' | 'closed'
  queueCapacity?: string
  bookingWindow?: string
}

export interface CategoryFieldConfig {
  sectionTitle: string
  statusTitle: string
  primaryTimeLabel: string
  primaryTimePlaceholder: string
  expectationLabel: string
  expectationPlaceholder: string
  originLabel: string
  originPlaceholder: string
  showOrigin: boolean
  showSubscriptionTiming: boolean
  showQueueFields: boolean
  scopePlaceholder: string
  timelinePlaceholder: string
  composerMessagePlaceholder: string
  delayLabels: { onTime: string; delayed: string; significantDelay: string }
}

export const getCategoryConfig = (
  category: VendorCategory = 'merch',
): CategoryFieldConfig => {
  switch (category) {
    case 'bookbox':
      return {
        sectionTitle: 'Fulfillment Settings',
        statusTitle: 'Fulfillment & Shipping Status',
        primaryTimeLabel: 'Average Production Time',
        primaryTimePlaceholder: 'e.g., Ships 15th of every month',
        expectationLabel: 'Current Customer Expectation',
        expectationPlaceholder: 'e.g., Currently shipping on schedule',
        originLabel: 'Shipping Origin',
        originPlaceholder: 'e.g., Ships from Portland, OR (USA)',
        showOrigin: true,
        showSubscriptionTiming: true,
        showQueueFields: false,
        scopePlaceholder: 'e.g., October Box, Special Edition Box',
        timelinePlaceholder: 'e.g., Shipping begins Oct 20',
        composerMessagePlaceholder: 'What do your subscribers need to know?',
        delayLabels: {
          onTime: 'On Time',
          delayed: 'Delayed',
          significantDelay: 'Significant Delays',
        },
      }
    case 'author-direct':
      return {
        sectionTitle: 'Fulfillment Settings',
        statusTitle: 'Fulfillment & Shipping Status',
        primaryTimeLabel: 'Average Processing Time',
        primaryTimePlaceholder: 'e.g., Ships within 3-5 business days',
        expectationLabel: 'Current Customer Expectation',
        expectationPlaceholder: 'e.g., Orders currently shipping within 1 week',
        originLabel: 'Shipping Origin',
        originPlaceholder: 'e.g., Ships from Portland, OR (USA)',
        showOrigin: true,
        showSubscriptionTiming: false,
        showQueueFields: false,
        scopePlaceholder: 'e.g., Signed Paperbacks, Pre-orders',
        timelinePlaceholder: 'e.g., Shipping begins Oct 20',
        composerMessagePlaceholder: 'What do your readers need to know?',
        delayLabels: {
          onTime: 'On Time',
          delayed: 'Delayed',
          significantDelay: 'Significant Delays',
        },
      }
    case 'service':
    case 'creative':
      return {
        sectionTitle: 'Availability Settings',
        statusTitle: 'Availability Status',
        primaryTimeLabel: 'Estimated Turnaround Time',
        primaryTimePlaceholder: 'e.g., 2-3 weeks per project',
        expectationLabel: 'Current Client Expectation',
        expectationPlaceholder: 'e.g., Currently booking for next month',
        originLabel: 'Location',
        originPlaceholder: '',
        showOrigin: false,
        showSubscriptionTiming: false,
        showQueueFields: true,
        scopePlaceholder: 'e.g., Dev Edits, All New Projects',
        timelinePlaceholder: 'e.g., Starting Nov 1',
        composerMessagePlaceholder: 'What do your clients need to know?',
        delayLabels: {
          onTime: 'Open',
          delayed: 'Limited',
          significantDelay: 'Closed',
        },
      }
    case 'merch':
    default:
      return {
        sectionTitle: 'Fulfillment Settings',
        statusTitle: 'Fulfillment & Shipping Status',
        primaryTimeLabel: 'Average Production Time',
        primaryTimePlaceholder: 'e.g., Made to order – 2–4 weeks',
        expectationLabel: 'Current Customer Expectation',
        expectationPlaceholder:
          'e.g., Orders currently ship ~4-6 weeks later than standard production time',
        originLabel: 'Shipping Origin',
        originPlaceholder: 'e.g., Ships from Portland, OR (USA)',
        showOrigin: true,
        showSubscriptionTiming: false,
        showQueueFields: false,
        scopePlaceholder: 'e.g., All Apparel Orders',
        timelinePlaceholder: 'e.g., Shipping begins Oct 20',
        composerMessagePlaceholder: 'What do your customers need to know?',
        delayLabels: {
          onTime: 'On Time',
          delayed: 'Delayed',
          significantDelay: 'Significant Delays',
        },
      }
  }
}

export const mockVendorFulfillmentSettings: Record<
  string,
  VendorFulfillmentSettings
> = {
  '1': {
    averageProductionTime: 'Made to order – 2–4 weeks',
    currentCustomerExpectation:
      'Orders currently ship ~4-6 weeks later than standard production time',
    shippingOrigin: 'Ships from Portland, OR (USA)',
  },
}

export const mockFulfillmentData: Record<string, FulfillmentTransparency> = {
  '1': {
    averageProductionTime: 'Made to order – 2–4 weeks',
    currentCustomerExpectation:
      'Orders currently ship ~4-6 weeks later than standard production time',
    shippingOrigin: 'Ships from Portland, OR (USA)',
    lastUpdated: '2023-10-15T10:00:00Z',
    delayStatus: 'delayed',
    communicationStatus: 'active',
    daysSinceLastUpdate: 12,
  },
}

export const mockVendorUpdates: Record<string, VendorUpdate[]> = {
  '1': [
    {
      id: 'u1',
      type: 'shipping-update',
      message:
        'Good news! The fabric for the Beyond the Veil tees has finally arrived. We are starting production tomorrow and expect to begin shipping by the end of the week.',
      estimatedNewTimeline: 'Shipping begins Oct 20',
      datePosted: '2023-10-15T14:30:00Z',
      affectedScope: 'Beyond the Veil Collection',
      affectedCollectionIds: ['c1'],
    },
    {
      id: 'u2',
      type: 'delay',
      message:
        'We are experiencing a slight delay with our screen printing partner due to equipment maintenance. This affects all apparel orders placed between Sept 15 and Oct 1.',
      estimatedNewTimeline: 'Additional 2 weeks',
      datePosted: '2023-10-05T09:15:00Z',
      affectedScope: 'Apparel Orders (Sep 15 - Oct 1)',
      isGlobal: false,
    },
    {
      id: 'u3',
      type: 'resolved',
      message:
        'All backlogged bookmark sets have now been shipped! Thank you for your patience while we waited for the new cardstock to arrive.',
      datePosted: '2023-09-28T16:45:00Z',
      affectedScope: 'Bookmark Sets',
    },
    {
      id: 'u4',
      type: 'production-update',
      message:
        'The new Fall Collection designs have been finalized and sent to the printer. We will be opening pre-orders next week.',
      datePosted: '2023-09-10T11:00:00Z',
      affectedScope: 'Fall Collection',
    },
  ],
}
